const userId = import.meta.env.VITE_OASIS_USER_ID; // Esto se borra luego

// React
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import type { FC } from "react";

// Google Maps Loader
const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
import LocationSearchMap from "../components/LocationSearchMap";
import Select from "react-select";

// Librerias externas
import { useDarkMode } from "../../../../context/ThemeContext";
import { useForm, Controller } from "react-hook-form";
import type { SweetAlertIcon } from "sweetalert2";

// Componentes
import CustomPhoneInput from "../../components/CustomPhoneInput";
import LoadingSpinner from "../../components/LoadingSpinner";
import { CustomAlert } from "../../components/CustomAlert";
import CustomButton from "../../components/CustomButton";
import { useJsApiLoader } from "@react-google-maps/api";
import BackButton from "../../components/BackButton";
import { useYardStore } from "../../../../store/oasis/yardsStore";

// Funciones
import {
  options,
  onCreate,
  onUpdate,
  isFormChanged,
  isFormValid,
} from "./functions";
import type { Yard } from "./functions";

// Estilos
import {
  FormContainer,
  Path,
  NavbarContainer,
  TittleContainer,
  NavbarTitle,
  Form,
  Section,
  SectionTitle,
  SectionContainer,
  ColumnContainer,
  InputContainer,
  InputLabel,
  Input,
  Error,
} from "./styles";

const YardsForm: FC = () => {
  // Hooks
  const { t } = useTranslation("yardsForm");
  const navigate = useNavigate();

  // Google Maps Loader
  const libraries: any = ["places", "geometry"];
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
    mapIds: ["86bc5ef6ec400969"],
    version: "weekly",
    language: "es",
  });

  // Modo oscuro
  const { isDarkMode } = useDarkMode();

  // Estados globales
  const { isEdit, setIsEdit, yardSelected, setYardSelected } = useYardStore();

  // Estados locales
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{
    visible: boolean;
    title?: string;
    text?: string;
    icon?: SweetAlertIcon;
    onConfirm?: () => void;
  }>({ visible: false });

  // React Hook Form
  const {
    register,
    reset,
    setValue,
    formState: { errors },
    clearErrors,
    setError,
    control,
    getValues,
    handleSubmit,
  } = useForm();

  const values = getValues();

  // FUNCIONES

  // Función para crear nuevo yard
  const handleCreate = async (data: any) => {
    setLoading(true);
    const yardData = {
      userId,
      yard: {
        resellerId: userId,
        name: data.name,
        location: {
          address: data.location?.address || "",
          centerLatitude: data.location?.centerLat || 0,
          centerLongitude: data.location?.centerLng || 0,
          entryLatitude: data.location?.entryLat || 0,
          entryLongitude: data.location?.entryLng || 0,
          mapLink: data.location?.mapLink || "",
          radius: data.location?.radius || 0,
        },
        phone: {
          areaCode: data?.areaCode || "MX",
          areaCodeNumber: data?.areaCodeNumber || "+52",
          number: data?.phone || "",
        },
        timeZone: data.timeZone,
      },
    };
    try {
      await onCreate(yardData, setAlert, reset, navigate, t);
    } finally {
      setLoading(false);
    }
  };

  // Función para actualizar yard
  const handleUpdate = async (data: any) => {
    // Limpieza de teléfono
    const cleanPhone = (phone: string, areaCodeNumber: string) => {
      let cleanedPhone = phone?.toString().replace(/[- ()]/g, "");
      if (cleanedPhone?.startsWith(areaCodeNumber)) {
        cleanedPhone = cleanedPhone?.slice(areaCodeNumber.length);
      }
      return cleanedPhone;
    };
    const yardData = {
      userId,
      yard: {
        id: yardSelected?.id || "",
        name: data.name,
        phone: {
          areaCode: data?.areaCode || "",
          areaCodeNumber: data?.areaCodeNumber || "",
          number: cleanPhone(data?.phone || "", data?.areaCodeNumber || ""),
        },
        timeZone: data.timeZone,
        location: {
          address: data.location?.address || "",
          centerLatitude: data.location?.centerLat || 0,
          centerLongitude: data.location?.centerLng || 0,
          entryLatitude: data.location?.entryLat || 0,
          entryLongitude: data.location?.entryLng || 0,
          mapLink: data.location?.mapLink || "",
          radius: data.location?.radius || 0,
        },
      },
    };
    await onUpdate(
      yardData,
      setAlert, // o dispatch, según tu lógica
      reset,
      navigate,
      isEdit,
      setIsEdit,
      t,
      setLoading
    );
    // Aquí iría la llamada a la API
  };

  // Manejar el submit desde botón externo
  const handleForm = () => {
    isEdit ? handleSubmit(handleUpdate)() : handleSubmit(handleCreate)();
  };

  // EFECTOS

  // Cargar datos del yard a editar
  useEffect(() => {
    if (isEdit && yardSelected) {
      setValue("name", yardSelected.name || "");
      setValue("phone", yardSelected.phone?.number || "");
      setValue("areaCode", yardSelected.phone?.areaCode || "MX");
      setValue("areaCodeNumber", yardSelected.phone?.areaCodeNumber || "+52");
      setValue("timeZone", yardSelected.timeZone || "");
      setValue("location", {
        address: yardSelected.location?.address || "",
        centerLat: yardSelected.location?.centerLatitude || 0,
        centerLng: yardSelected.location?.centerLongitude || 0,
        entryLat: yardSelected.location?.entryLatitude || 0,
        entryLng: yardSelected.location?.entryLongitude || 0,
        mapLink: yardSelected.location?.mapLink || "",
        radius: yardSelected.location?.radius || 0,
      });
    } else {
      // Inicialización para crear
      setValue("timeZone", "");
    }
  }, [isEdit, yardSelected, setValue, t]);

  // Persistir cambios en localStorage
  useEffect(() => {
    if (isEdit && yardSelected) {
      localStorage.setItem("isEditYard", JSON.stringify(isEdit));
      localStorage.setItem("yardSelected", JSON.stringify(yardSelected));
    }
  }, [isEdit, yardSelected]);

  // Restaurar valores al montar el componente
  useEffect(() => {
    const storedIsEdit = localStorage.getItem("isEditYard");
    const storedYard = localStorage.getItem("yardSelected");
    if (storedIsEdit && storedYard) {
      setIsEdit(JSON.parse(storedIsEdit));
      setYardSelected(JSON.parse(storedYard));
    }
  }, [setIsEdit, setYardSelected]);

  const changedValues = {
    name: values.name || "",
    phone: values.phone || "",
    timeZone: values.timeZone || "",
    location: values.location || { address: "" },
  };

  const isDisabled = isEdit
    ? !isFormChanged(isEdit, yardSelected, changedValues)
    : !isFormValid(changedValues);

  return (
    <FormContainer
      className={
        isDarkMode ? "bg-dark-surface text-white" : "bg-white text-gray-900"
      }
    >
      {loading && <LoadingSpinner />}
      {alert.visible && (
        <CustomAlert
          title={alert.title || ""}
          text={alert.text || ""}
          icon={alert.icon ?? "info"}
          confirmButtonText={"OK"}
          confirmButtonColor={"#0052A2"}
          onConfirm={() => alert.onConfirm && alert.onConfirm()}
          showCancelButton={false}
        />
      )}
      <Path className={isDarkMode ? "text-gray-400" : "text-gray-400"}>
        Patios / Formulario
      </Path>
      <NavbarContainer>
        <TittleContainer>
          <BackButton
            handleClick={() => {
              setIsEdit(false);
              setYardSelected(null);
              localStorage.removeItem("isEditYard");
              localStorage.removeItem("yardSelected");
              navigate("/oasis/yards");
            }}
          />
          <NavbarTitle
            className={isDarkMode ? "text-[#1C93FF]" : "text-[#0052A2]"}
          >
            {isEdit ? t("editYard") : t("createYard")}
          </NavbarTitle>
        </TittleContainer>
        <CustomButton
          id="saveYard"
          // disable={isDisabled}
          handleClick={handleForm}
          styleRendering={
            isDarkMode
              ? "bg-[#1C93FF] hover:bg-[#197CD6] text-white"
              : "bg-[#0052A2] hover:bg-[#1C93FF] text-white"
          }
          text={isEdit ? t("saveChanges") : t("createYard")}
        />
      </NavbarContainer>
      <Form
        onSubmit={
          isEdit ? handleSubmit(handleUpdate) : handleSubmit(handleCreate)
        }
      >
        <Section className={isDarkMode ? "bg-[#232323]" : "bg-white"}>
          <SectionTitle
            className={isDarkMode ? "text-[#1C93FF]" : "text-[#616161]"}
          >
            Información básica
          </SectionTitle>
          <SectionContainer>
            <ColumnContainer>
              <InputContainer>
                <InputLabel
                  htmlFor="name"
                  className={isDarkMode ? "text-white" : "text-gray-900"}
                >
                  Nombre del patio
                </InputLabel>
                <Input
                  type="text"
                  id="name"
                  {...register("name", { required: t("requiredField") })}
                  name="name"
                  placeholder={t("placeHholderName")}
                  defaultValue={isEdit ? yardSelected?.name : ""}
                  className={`${
                    isDarkMode
                      ? "bg-[#232323] text-white border-dark-border"
                      : "bg-white text-gray-900 border-[#DFE5EF]"
                  } ${
                    errors.name
                      ? "border-red-800 focus:border-red-800"
                      : "hover:border-[#1C93FF] focus:border-[#1C93FF]"
                  }`}
                />
                {errors?.name && <Error>{errors?.name?.message}</Error>}
              </InputContainer>
              <InputContainer>
                <InputLabel
                  htmlFor="phone"
                  className={isDarkMode ? "text-white" : "text-gray-900"}
                >
                  {t("phoneNumber")}
                </InputLabel>
                <CustomPhoneInput
                  register={register}
                  isEdit={isEdit}
                  objData={yardSelected as Yard | null}
                  t={t}
                  errors={errors}
                  clearErrors={clearErrors}
                  setError={setError}
                  setValue={setValue}
                  fieldName="phone"
                  placeholder={t("placeholderPhoneYard")}
                  isDarkMode={isDarkMode}
                />
                {errors?.phone && <Error>{errors?.phone?.message}</Error>}
              </InputContainer>

              {/* Input / Zona Horaria */}
              <InputContainer>
                <InputLabel
                  htmlFor="yardTimeZone"
                  className={isDarkMode ? "text-white" : "text-gray-900"}
                >
                  {t("timeZone")}
                </InputLabel>
                <Controller
                  name="timeZone"
                  control={control}
                  rules={{ required: t("requiredField") }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      id="timeZone"
                      name="timeZone"
                      options={options(t)}
                      value={options(t).find(
                        (option) => option.value === field.value
                      )}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          cursor: "pointer",
                          width: "100%",
                          height: "2.75rem",
                          border: errors.timeZone
                            ? "2px solid #991b1b"
                            : isDarkMode
                            ? "2px solid #4A4A4A"
                            : "2px solid #DFE5EF",
                          borderRadius: "0.5rem",
                          boxShadow: "none",
                          fontSize: "15px",
                          marginLeft: 0,
                          color: isDarkMode ? "#fff" : "#0C0A09",
                          backgroundColor: isDarkMode ? "#232323" : "#fff",
                          "&:hover": {
                            borderColor: errors.timeZone
                              ? "#991b1b"
                              : "#1C93FF",
                          },
                          "&:focus": {
                            borderColor: errors.timeZone
                              ? "#991b1b"
                              : "#1C93FF",
                          },
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          cursor: "pointer",
                          fontSize: "15px",
                          color: isDarkMode ? "#fff" : "#0C0A09",
                          backgroundColor: state.isFocused
                            ? isDarkMode
                              ? "#3A3A3A"
                              : "#E3E8F0" // más claro en hover
                            : isDarkMode
                            ? "#232323"
                            : "#F3F4F6",
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: isDarkMode ? "#fff" : "#0C0A09",
                        }),
                        input: (provided) => ({
                          ...provided,
                          color: isDarkMode ? "#fff" : "#0C0A09",
                        }),
                        placeholder: (provided) => ({
                          ...provided,
                          color: isDarkMode ? "#bdbdbd" : "#616161",
                        }),
                      }}
                      isSearchable={false}
                      onChange={(option) => field.onChange(option?.value)}
                      onBlur={field.onBlur}
                      placeholder={"Selecciona zona horaria"}
                    />
                  )}
                />
                {errors.timeZone && <Error>{errors.timeZone.message}</Error>}
              </InputContainer>
            </ColumnContainer>
            <ColumnContainer>
              <InputContainer>
                <InputLabel
                  htmlFor="location"
                  className={isDarkMode ? "text-white" : "text-gray-900"}
                >
                  {t("location")}
                </InputLabel>
                {isLoaded && (
                  <LocationSearchMap
                    control={control}
                    errors={errors}
                    t={t}
                    setValueForm={setValue}
                    isEdit={isEdit}
                    objYard={yardSelected as Yard | null}
                    isLoaded={isLoaded}
                    getValues={getValues}
                    isDarkMode={isDarkMode}
                  />
                )}
              </InputContainer>
            </ColumnContainer>
          </SectionContainer>
        </Section>
      </Form>
    </FormContainer>
  );
};

export default YardsForm;
